import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/TradeAuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "@/components/ui/select";
import {
  Plus, Handshake, Trash2, ArrowLeft, Save, Star, ShoppingBag,
  Phone, Mail, MapPin, MessageSquare, MessageCircle, Send, Landmark,
  Megaphone, Square, CheckSquare, Copy, X, Zap, CheckCircle2, XCircle
} from "lucide-react";
import { toast } from "sonner";
import CallPrompt from "@/pages/trade/CallPrompt";
import ContactAudioRecordButton from "@/pages/trade/ContactAudioRecordButton";
import RecordedCallButton from "@/pages/trade/RecordedCallButton";

export const BrokersList = () => {
  const { API, authHeader } = useAuth();
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const fetchData = () => {
    axios.get(`${API}/trade/brokers`, { headers: authHeader() })
      .then(res => setBrokers(res.data))
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this broker?")) return;
    try {
      await axios.delete(`${API}/trade/brokers/${id}`, { headers: authHeader() });
      toast.success("Broker deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const waLink = (num) => `https://wa.me/${(num || "").replace(/[^0-9]/g, "")}`;
  const tgLink = (t) => {
    const v = (t || "").trim();
    if (!v) return "";
    if (v.startsWith("@")) return `https://t.me/${v.slice(1)}`;
    return `https://t.me/+${v.replace(/[^0-9]/g, "")}`;
  };

  const filtered = brokers.filter(b => filter === "all" || (b.broker_type || "sales") === filter);

  // Bulk messaging state + helpers
  const [selected, setSelected] = useState([]);
  const [composeOpen, setComposeOpen] = useState(false);
  const [message, setMessage] = useState("Goldspot Trade — Goods Available for Sale\n\nContact us for pricing and availability.");
  const [copied, setCopied] = useState(false);
  const [sendMethod, setSendMethod] = useState("sms");
  const [blastSending, setBlastSending] = useState(false);
  const [blastResult, setBlastResult] = useState(null);

  const sendTwilioBlast = async () => {
    if (!message.trim()) {
      toast.error("Type a message first");
      return;
    }
    setBlastSending(true);
    setBlastResult(null);
    try {
      const res = await axios.post(`${API}/trade/brokers/text-blast`, {
        message, broker_ids: selected
      }, { headers: authHeader() });
      setBlastResult(res.data);
      if (res.data.failed === 0) {
        toast.success(`Sent to all ${res.data.sent} brokers`);
      } else {
        toast.warning(`Sent ${res.data.sent}, failed ${res.data.failed}`);
      }
    } catch (e) {
      const detail = e.response?.data?.detail || "Failed to send";
      toast.error(detail);
      setBlastResult({ error: detail });
    } finally {
      setBlastSending(false);
    }
  };

  const sendWhatsAppBlast = async () => {
    if (!message.trim()) {
      toast.error("Type a message first");
      return;
    }
    setBlastSending(true);
    setBlastResult(null);
    try {
      const res = await axios.post(`${API}/trade/brokers/whatsapp-blast`, {
        message, broker_ids: selected
      }, { headers: authHeader() });
      setBlastResult(res.data);
      if (res.data.failed === 0) {
        toast.success(`WhatsApp sent to ${res.data.sent} broker${res.data.sent !== 1 ? "s" : ""}`);
      } else {
        toast.warning(`Sent ${res.data.sent}, failed ${res.data.failed}`);
      }
    } catch (e) {
      const detail = e.response?.data?.detail || "Failed to send";
      toast.error(detail);
      setBlastResult({ error: detail });
    } finally {
      setBlastSending(false);
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const allSelected = filtered.length > 0 && filtered.every(b => selected.includes(b.id));
  const toggleSelectAll = () => {
    setSelected(allSelected ? [] : filtered.map(b => b.id));
  };
  const selectedBrokers = brokers.filter(b => selected.includes(b.id));
  const selectedPhones = selectedBrokers.map(b => b.phone).filter(Boolean);
  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  return (
    <div className="trade-section pt-32 lg:pt-20 pb-20 min-h-screen">
      <div className="lg:ml-64 px-6 md:px-12 py-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-start justify-between flex-wrap gap-4 mb-10"
        >
          <div>
            <p className="text-xs uppercase tracking-[0.3em] trade-gold mb-3">Brokers</p>
            <h1 className="text-4xl md:text-5xl font-serif font-bold">Ethiopian Broker Agents</h1>
            <p className="text-white/50 mt-2">Track sales brokers, bankers and buyers — advertise your goods by text or email when you have a sale</p>
          </div>
          <Link to="/trade/brokers/new">
            <Button className="trade-btn-primary px-6 py-6 rounded-sm" data-testid="new-broker-btn">
              <Plus size={18} className="mr-2" /> New Broker
            </Button>
          </Link>
        </motion.div>

        {/* Type filter tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {[
            { value: "all", label: "All", count: brokers.length },
            { value: "sales", label: "Sales Brokers", count: brokers.filter(b => (b.broker_type || "sales") === "sales").length },
            { value: "banker", label: "Bankers", count: brokers.filter(b => b.broker_type === "banker").length },
            { value: "buyer", label: "Buyers", count: brokers.filter(b => b.broker_type === "buyer").length },
          ].map(t => (
            <button
              key={t.value}
              onClick={() => setFilter(t.value)}
              className={`px-4 py-2 text-xs rounded-sm whitespace-nowrap transition-all ${
                filter === t.value
                  ? "bg-[#D4AF37] text-black font-semibold"
                  : "bg-white/5 text-white/60 hover:text-white"
              }`}
              data-testid={`filter-${t.value}`}
            >
              {t.label} ({t.count})
            </button>
          ))}
        </div>

        {/* Bulk messaging bar */}
        <div className="trade-card rounded-sm p-4 mb-6 flex items-center justify-between flex-wrap gap-3">
          <button
            onClick={toggleSelectAll}
            className="flex items-center gap-2 text-xs text-white/70 hover:text-white transition-colors"
            data-testid="select-all-btn"
          >
            {allSelected
              ? <CheckSquare size={16} className="trade-gold" />
              : <Square size={16} />
            }
            {allSelected ? "Deselect All" : "Select All"}
          </button>
          <div className="flex items-center gap-3">
            <span className="text-xs text-white/50" data-testid="selected-count">
              {selected.length} selected
            </span>
            <Button
              onClick={() => setComposeOpen(true)}
              disabled={selected.length === 0}
              className="trade-btn-primary rounded-sm h-9 px-4 text-xs"
              data-testid="compose-btn"
            >
              <Megaphone size={14} className="mr-2" /> Text / Advertise
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center p-20 text-white/40">Loading...</div>
        ) : filtered.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="trade-card rounded-sm p-16 text-center"
          >
            <Handshake className="w-16 h-16 trade-gold mx-auto mb-4 opacity-40" strokeWidth={1} />
            <h3 className="text-xl font-serif mb-2">
              {brokers.length === 0 ? "No brokers yet" : "No brokers in this category"}
            </h3>
            <p className="text-white/50 mb-6">
              {brokers.length === 0
                ? "Add Ethiopian brokers, bankers and buyers so you can advertise goods, process bank permits and track who purchases your goods"
                : "Try another category, or add a new one"}
            </p>
            <Link to="/trade/brokers/new">
              <Button className="trade-btn-primary rounded-sm">
                <Plus size={16} className="mr-2" /> Add Broker
              </Button>
            </Link>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((b, i) => (
              <motion.div
                key={b.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <div className="trade-card rounded-sm p-6" data-testid={`broker-card-${b.id}`}>
                  <Link to={`/trade/brokers/${b.id}`} className="group block">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleSelect(b.id); }}
                          className="text-white/50 hover:text-[#D4AF37] transition-colors"
                          data-testid={`broker-checkbox-${b.id}`}
                          title="Select for bulk message"
                        >
                          {selected.includes(b.id)
                            ? <CheckSquare size={18} className="trade-gold" />
                            : <Square size={18} />
                          }
                        </button>
                        <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${
                          b.broker_type === "banker" ? "bg-[#24A1DE]/10" : b.broker_type === "buyer" ? "bg-[#D4AF37]/10" : "bg-[#10b981]/10"
                        }`}>
                          {b.broker_type === "banker"
                            ? <Landmark className="w-6 h-6 text-[#24A1DE]" strokeWidth={1.5} />
                            : b.broker_type === "buyer"
                            ? <ShoppingBag className="w-6 h-6 trade-gold" strokeWidth={1.5} />
                            : <Handshake className="w-6 h-6 trade-green" strokeWidth={1.5} />
                          }
                        </div>
                      </div>
                      <div className="flex gap-1">
                        {[...Array(5)].map((_, idx) => (
                          <Star key={idx} size={12} className={idx < (b.rating || 0) ? "fill-[#D4AF37] text-[#D4AF37]" : "text-white/20"} />
                        ))}
                      </div>
                    </div>

                    <h3 className="font-serif font-semibold text-lg mb-1">{b.name}</h3>
                    <p className={`text-[10px] uppercase tracking-widest mb-2 ${
                      b.broker_type === "banker" ? "text-[#24A1DE]" : b.broker_type === "buyer" ? "trade-gold" : "trade-green"
                    }`} data-testid={`broker-type-${b.id}`}>
                      {b.broker_type === "banker"
                        ? `Banker · Bank Permit${b.bank_name ? ` · ${b.bank_name}` : ""}`
                        : b.broker_type === "buyer"
                        ? "Buyer"
                        : "Sales Broker"}
                    </p>
                    {b.company && <p className="text-xs text-white/60 mb-2">{b.company}</p>}
                    {b.city && (
                      <p className="text-xs text-white/50 flex items-center gap-1 mb-3">
                        <MapPin size={10} /> {b.city}
                      </p>
                    )}

                    <div className="space-y-2 pt-4 border-t border-white/5">
                      {b.phone && (
                        <p className="text-xs text-white/50 flex items-center gap-2">
                          <Phone size={10} /> {b.phone}
                        </p>
                      )}
                      {b.email && (
                        <p className="text-xs text-white/50 flex items-center gap-2">
                          <Mail size={10} /> {b.email}
                        </p>
                      )}
                      {b.telegram && (
                        <p className="text-xs text-white/50 flex items-center gap-2">
                          <Send size={10} /> {b.telegram}
                        </p>
                      )}
                    </div>
                  </Link>

                  {/* Advertise quick actions */}
                  <div className="flex gap-2 mt-4 pt-4 border-t border-white/5">
                    {b.phone && (
                      <CallPrompt
                        phone={b.phone}
                        name={b.name}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-white/5 text-white/70 hover:bg-white/10 transition-colors"
                        testid={`call-broker-${b.id}`}
                      />
                    )}
                    <ContactAudioRecordButton
                      phone={b.phone}
                      name={b.name}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#D4AF37]/10 trade-gold hover:bg-[#D4AF37]/20 transition-colors"
                      testid={`audio-broker-${b.id}`}
                    />
                    {b.phone && (
                      <RecordedCallButton
                        phone={b.phone}
                        name={b.name}
                        contactId={b.id}
                        contactType="broker"
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-red-500/10 text-red-300 hover:bg-red-500/20 transition-colors"
                        testid={`record-call-broker-${b.id}`}
                      />
                    )}
                    {b.phone && (
                      <a
                        href={`sms:${b.phone}`}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#D4AF37]/10 trade-gold hover:bg-[#D4AF37]/20 transition-colors"
                        data-testid={`text-broker-${b.id}`}
                      >
                        <MessageSquare size={12} /> Text
                      </a>
                    )}
                    {b.email && (
                      <a
                        href={`mailto:${b.email}`}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#D4AF37]/10 trade-gold hover:bg-[#D4AF37]/20 transition-colors"
                        data-testid={`email-broker-${b.id}`}
                      >
                        <Mail size={12} /> Email
                      </a>
                    )}
                    {b.whatsapp && (
                      <a
                        href={waLink(b.whatsapp)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#10b981]/10 trade-green hover:bg-[#10b981]/20 transition-colors"
                        data-testid={`whatsapp-broker-${b.id}`}
                      >
                        <MessageCircle size={12} /> WhatsApp
                      </a>
                    )}
                    {b.telegram && (
                      <a
                        href={tgLink(b.telegram)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#24A1DE]/10 text-[#24A1DE] hover:bg-[#24A1DE]/20 transition-colors"
                        data-testid={`telegram-broker-${b.id}`}
                      >
                        <Send size={12} /> Telegram
                      </a>
                    )}
                    <button
                      onClick={() => handleDelete(b.id)}
                      className="px-3 py-2 text-xs text-red-400/60 hover:text-red-400 transition-colors"
                      data-testid={`delete-broker-${b.id}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
        {/* Bulk compose modal */}
        {composeOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={() => setComposeOpen(false)}
            data-testid="compose-modal"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              className="trade-card rounded-sm p-6 w-full max-w-lg max-h-[85vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-sm flex items-center justify-center">
                    <Megaphone className="w-5 h-5 trade-gold" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-serif font-semibold text-lg">Message Brokers</h3>
                    <p className="text-xs text-white/50">{selected.length} selected</p>
                  </div>
                </div>
                <button
                  onClick={() => setComposeOpen(false)}
                  className="p-2 text-white/40 hover:text-white transition-colors"
                  data-testid="compose-close-btn"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="flex items-center justify-between mb-2">
                <p className="text-xs uppercase tracking-widest text-white/40">Your message</p>
                <button
                  onClick={copyMessage}
                  className="flex items-center gap-1 text-xs trade-gold hover:underline"
                  data-testid="compose-copy-btn"
                >
                  {copied ? <CheckSquare size={12} /> : <Copy size={12} />} {copied ? "Copied" : "Copy"}
                </button>
              </div>
              <Textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                rows={5}
                placeholder="Type your message once — then send to all selected brokers or one by one"
                className="trade-input rounded-sm mb-5"
                data-testid="compose-message-input"
              />

              {/* Send method selector */}
              <p className="text-xs uppercase tracking-widest text-white/40 mb-2">Send method</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {[
                  { value: "sms", label: "Normal Text", icon: MessageSquare },
                  { value: "whatsapp", label: "WhatsApp", icon: MessageCircle },
                  { value: "telegram", label: "Telegram", icon: Send },
                  { value: "twilio", label: "Real SMS", icon: Zap },
                  { value: "wablast", label: "WA Blast", icon: Megaphone },
                ].map(m => (
                  <button
                    key={m.value}
                    onClick={() => setSendMethod(m.value)}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs rounded-sm transition-all ${
                      sendMethod === m.value
                        ? "bg-[#D4AF37] text-black font-semibold"
                        : "bg-white/5 text-white/60 hover:text-white"
                    }`}
                    data-testid={`method-${m.value}`}
                  >
                    <m.icon size={12} /> {m.label}
                  </button>
                ))}
              </div>

              {sendMethod === "sms" && selectedPhones.length > 0 && (
                <a
                  href={`sms:${selectedPhones.join(",")}?&body=${encodeURIComponent(message)}`}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm rounded-sm trade-btn-primary"
                  data-testid="text-all-btn"
                >
                  <MessageSquare size={16} /> Text All {selectedPhones.length} in One Message
                </a>
              )}
              {sendMethod === "whatsapp" && (
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(message)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm rounded-sm bg-[#10b981] text-black font-semibold hover:bg-[#0da06a] transition-colors"
                  data-testid="whatsapp-all-btn"
                >
                  <MessageCircle size={16} /> WhatsApp All {selected.length} Selected
                </a>
              )}
              {sendMethod === "telegram" && (
                <a
                  href={`https://t.me/share/url?url=&text=${encodeURIComponent(message)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm rounded-sm bg-[#24A1DE] text-white font-semibold hover:bg-[#1e8fc5] transition-colors"
                  data-testid="telegram-all-btn"
                >
                  <Send size={16} /> Telegram All {selected.length} Selected
                </a>
              )}
              {sendMethod === "twilio" && (
                <div>
                  <Button
                    onClick={sendTwilioBlast}
                    disabled={blastSending || selected.length === 0}
                    className="w-full flex items-center justify-center gap-2 px-4 py-6 text-sm rounded-sm trade-btn-primary"
                    data-testid="twilio-send-btn"
                  >
                    <Zap size={16} />
                    {blastSending ? "Sending..." : `Send Real SMS to ${selected.length} Broker${selected.length !== 1 ? "s" : ""}`}
                  </Button>
                  {blastResult && !blastResult.error && (
                    <div className="mt-3 space-y-1.5" data-testid="twilio-results">
                      <p className="text-[11px] text-white/50 mb-1">
                        Sent {blastResult.sent} · Failed {blastResult.failed} (Twilio trial can only text verified numbers)
                      </p>
                      {blastResult.results.map((r, i) => (
                        <div key={i} className="flex items-start gap-2 text-[11px] bg-white/5 rounded-sm px-3 py-2" data-testid={`twilio-result-${i}`}>
                          {r.success
                            ? <CheckCircle2 size={13} className="trade-green flex-shrink-0 mt-0.5" />
                            : <XCircle size={13} className="text-red-400 flex-shrink-0 mt-0.5" />
                          }
                          <div className="min-w-0">
                            <span className="text-white/80">{r.name} · {r.phone}</span>
                            <span className={r.success ? " trade-green" : " text-red-400/90 block break-all"}>
                              {r.success ? ` — ${r.status}` : ` — ${r.error}`}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {blastResult?.error && (
                    <p className="mt-3 text-[11px] text-red-400 bg-red-400/10 rounded-sm px-3 py-2" data-testid="twilio-error">
                      {blastResult.error}
                    </p>
                  )}
                </div>
              )}
              {sendMethod === "wablast" && (
                <div>
                  <Button
                    onClick={sendWhatsAppBlast}
                    disabled={blastSending || selected.length === 0}
                    className="w-full flex items-center justify-center gap-2 px-4 py-6 text-sm rounded-sm bg-[#10b981] text-black font-semibold hover:bg-[#0da06a] transition-colors"
                    data-testid="wablast-send-btn"
                  >
                    <Megaphone size={16} />
                    {blastSending ? "Sending..." : `WhatsApp Blast to ${selected.length} Broker${selected.length !== 1 ? "s" : ""}`}
                  </Button>
                  <p className="text-[11px] text-amber-400/90 mt-2" data-testid="wablast-sandbox-hint">
                    Sandbox mode: each phone must first send the join code to the Twilio sandbox on WhatsApp once, or the message will fail.
                  </p>
                  {blastResult && !blastResult.error && (
                    <div className="mt-3 space-y-1.5" data-testid="wablast-results">
                      <p className="text-[11px] text-white/50 mb-1">
                        Sent {blastResult.sent} · Failed {blastResult.failed} — delivery status updates automatically
                      </p>
                      {blastResult.results.map((r, i) => (
                        <div key={i} className="flex items-start gap-2 text-[11px] bg-white/5 rounded-sm px-3 py-2" data-testid={`wablast-result-${i}`}>
                          {r.success
                            ? <CheckCircle2 size={13} className="trade-green flex-shrink-0 mt-0.5" />
                            : <XCircle size={13} className="text-red-400 flex-shrink-0 mt-0.5" />
                          }
                          <div className="min-w-0">
                            <span className="text-white/80">{r.name} · {r.phone}</span>
                            <span className={r.success ? " trade-green" : " text-red-400/90 block break-all"}>
                              {r.success ? ` — ${r.status}` : ` — ${r.error}`}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {blastResult?.error && (
                    <p className="mt-3 text-[11px] text-red-400 bg-red-400/10 rounded-sm px-3 py-2" data-testid="wablast-error">
                      {blastResult.error}
                    </p>
                  )}
                </div>
              )}
              <p className="text-[11px] text-white/40 mt-2 mb-5">
                {sendMethod === "sms" && "Opens your messaging app with all numbers in one group text."}
                {sendMethod === "whatsapp" && "Opens WhatsApp — tap the selected brokers' chats to send (WhatsApp does not allow auto-filling multiple recipients)."}
                {sendMethod === "telegram" && "Opens Telegram — choose the chats to forward the message to."}
                {sendMethod === "twilio" && "The app sends real SMS from the server — your phone is not involved. Amharic supported."}
                {sendMethod === "wablast" && "The app sends real WhatsApp messages from the server via Twilio. Amharic supported."}
              </p>

              <p className="text-xs uppercase tracking-widest text-white/40 mb-3">Or send one by one</p>
              <div className="space-y-3">
                {selectedBrokers.map(b => (
                  <div
                    key={b.id}
                    className="flex items-center justify-between gap-3 p-3 bg-white/5 rounded-sm"
                    data-testid={`compose-broker-${b.id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate">{b.name}</p>
                      <p className="text-[11px] text-white/40 truncate">
                        {b.broker_type === "banker" ? `Banker${b.bank_name ? ` · ${b.bank_name}` : ""}` : b.broker_type === "buyer" ? (b.city || "Buyer") : (b.city || "Sales Broker")}
                      </p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0 flex-wrap justify-end">
                      {b.phone && (
                        <a
                          href={`sms:${b.phone}?&body=${encodeURIComponent(message)}`}
                          className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#D4AF37]/10 trade-gold hover:bg-[#D4AF37]/20 transition-colors"
                          data-testid={`compose-text-${b.id}`}
                        >
                          <MessageSquare size={12} /> Text
                        </a>
                      )}
                      {(b.whatsapp || b.phone) && (
                        <a
                          href={`${waLink(b.whatsapp || b.phone)}?text=${encodeURIComponent(message)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#10b981]/10 trade-green hover:bg-[#10b981]/20 transition-colors"
                          data-testid={`compose-whatsapp-${b.id}`}
                        >
                          <MessageCircle size={12} /> WhatsApp
                        </a>
                      )}
                      {b.telegram && (
                        <a
                          href={tgLink(b.telegram)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#24A1DE]/10 text-[#24A1DE] hover:bg-[#24A1DE]/20 transition-colors"
                          data-testid={`compose-telegram-${b.id}`}
                        >
                          <Send size={12} /> Telegram
                        </a>
                      )}
                      {b.email && (
                        <a
                          href={`mailto:${b.email}?subject=${encodeURIComponent("Goldspot Trade — Goods Available")}&body=${encodeURIComponent(message)}`}
                          className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#D4AF37]/10 trade-gold hover:bg-[#D4AF37]/20 transition-colors"
                          data-testid={`compose-email-${b.id}`}
                        >
                          <Mail size={12} /> Email
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export const BrokerForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { API, authHeader } = useAuth();
  const isEdit = !!id && id !== "new";

  const [form, setForm] = useState({
    name: "", broker_type: "sales", bank_name: "", company: "", phone: "", email: "",
    whatsapp: "", telegram: "", city: "", notes: "", rating: 5
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEdit) {
      axios.get(`${API}/trade/brokers`, { headers: authHeader() })
        .then(res => {
          const found = res.data.find(b => b.id === id);
          if (found) setForm({ broker_type: "sales", bank_name: "", ...found });
        });
    }
  }, [id]);

  const handleSave = async () => {
    if (!form.name) {
      toast.error("Broker name is required");
      return;
    }
    if (!form.phone && !form.email) {
      toast.error("Add at least a phone number or an email so you can advertise to this broker");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/trade/brokers/${id}`, form, { headers: authHeader() });
        toast.success("Broker updated");
      } else {
        await axios.post(`${API}/trade/brokers`, form, { headers: authHeader() });
        toast.success("Broker created");
      }
      navigate("/trade/brokers");
    } catch {
      toast.error("Failed to save broker");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="trade-section pt-32 lg:pt-20 pb-20 min-h-screen">
      <div className="lg:ml-64 px-6 md:px-12 py-10 max-w-3xl">
        <Link to="/trade/brokers" className="inline-flex items-center gap-2 text-sm text-white/60 hover:trade-gold mb-6">
          <ArrowLeft size={16} /> Back
        </Link>
        <h1 className="text-3xl md:text-4xl font-serif font-bold mb-2">
          {isEdit ? "Edit" : "New"} Broker
        </h1>
        <p className="text-white/50 text-sm mb-8">Ethiopian broker agent you advertise goods to by text or email</p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="trade-card rounded-sm p-8 space-y-5"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-white/60">Broker Type *</Label>
              <Select value={form.broker_type} onValueChange={v => setForm({...form, broker_type: v})}>
                <SelectTrigger className="trade-input mt-1 h-11 rounded-sm" data-testid="broker-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#142039] border-white/10">
                  <SelectItem value="sales">Sales Broker (advertise goods)</SelectItem>
                  <SelectItem value="banker">Banker (bank permit processing)</SelectItem>
                  <SelectItem value="buyer">Buyer (purchases your goods)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.broker_type === "banker" && (
              <div>
                <Label className="text-xs text-white/60">Bank Name</Label>
                <Input value={form.bank_name} onChange={e => setForm({...form, bank_name: e.target.value})} placeholder="e.g. Commercial Bank of Ethiopia, Awash Bank" className="trade-input mt-1 h-11 rounded-sm" data-testid="bank-name-input" />
              </div>
            )}
            <div>
              <Label className="text-xs text-white/60">{form.broker_type === "banker" ? "Banker Name *" : form.broker_type === "buyer" ? "Buyer Name *" : "Broker Name *"}</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="trade-input mt-1 h-11 rounded-sm" data-testid="broker-name-input" />
            </div>
            <div>
              <Label className="text-xs text-white/60">Company</Label>
              <Input value={form.company} onChange={e => setForm({...form, company: e.target.value})} className="trade-input mt-1 h-11 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs text-white/60">Phone (for SMS ads)</Label>
              <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+251 9X XXX XXXX" className="trade-input mt-1 h-11 rounded-sm" data-testid="broker-phone-input" />
            </div>
            <div>
              <Label className="text-xs text-white/60">Email (for email ads)</Label>
              <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="trade-input mt-1 h-11 rounded-sm" data-testid="broker-email-input" />
            </div>
            <div>
              <Label className="text-xs text-white/60">WhatsApp</Label>
              <Input value={form.whatsapp} onChange={e => setForm({...form, whatsapp: e.target.value})} placeholder="+251 9X XXX XXXX" className="trade-input mt-1 h-11 rounded-sm" data-testid="broker-whatsapp-input" />
            </div>
            <div>
              <Label className="text-xs text-white/60">Telegram</Label>
              <Input value={form.telegram} onChange={e => setForm({...form, telegram: e.target.value})} placeholder="@username or +251 9X XXX XXXX" className="trade-input mt-1 h-11 rounded-sm" data-testid="broker-telegram-input" />
            </div>
            <div>
              <Label className="text-xs text-white/60">City</Label>
              <Input value={form.city} onChange={e => setForm({...form, city: e.target.value})} placeholder="e.g. Addis Ababa, Dire Dawa" className="trade-input mt-1 h-11 rounded-sm" data-testid="broker-city-input" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-white/60">Rating</Label>
              <div className="flex gap-2 mt-2">
                {[1,2,3,4,5].map(n => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setForm({...form, rating: n})}
                    className="transition-transform hover:scale-110"
                    data-testid={`broker-rating-${n}`}
                  >
                    <Star size={24} className={n <= form.rating ? "fill-[#D4AF37] text-[#D4AF37]" : "text-white/20"} />
                  </button>
                ))}
              </div>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-white/60">Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={4} placeholder="What goods they move, typical buyers, commission terms..." className="trade-input mt-1 rounded-sm" data-testid="broker-notes-input" />
            </div>
          </div>

          <Button onClick={handleSave} disabled={saving} className="w-full trade-btn-primary rounded-sm h-12" data-testid="save-broker-btn">
            <Save size={16} className="mr-2" />
            {saving ? "Saving..." : (isEdit ? "Update" : "Create") + " Broker"}
          </Button>
        </motion.div>
      </div>
    </div>
  );
};
